/**
 * The set of statuses that represent a "signed" signature slot — i.e. the signer
 * has completed their action (signed directly, is waiting for witnesses, or has
 * reached the final completed state).
 *
 * These string values mirror `DocumentWorkflowInstanceSignatureStatus`
 * (SIGNED / WAITING_FOR_WITNESS_SIGNATURES / COMPLETED). They are intentionally
 * hardcoded rather than imported from `@dealstack/shared/lib/generated`: this
 * module is loaded by the backend at boot (via the claim/signing services), and
 * a runtime value-import of the generated enum fails during `generate-swagger`,
 * which boots the app before the generated types exist. The DB enum values are
 * stable, so drift risk is negligible.
 */
export const SIGNED_SIGNATURE_STATUSES: ReadonlySet<string> = new Set<string>([
	'SIGNED',
	'WAITING_FOR_WITNESS_SIGNATURES',
	'COMPLETED',
]);

/**
 * Returns true when `status` is one of the three "signed" states.
 *
 * The parameter is typed as `string | null | undefined` so that both the Prisma
 * `DocumentWorkflowInstanceSignatureStatus` (backend) and the OpenAPI-generated
 * one (frontend) can be passed directly — no cast required (string enum members
 * are assignable to `string`).
 */
export function isSignedSignatureStatus(status: string | null | undefined): boolean {
	return status != null && SIGNED_SIGNATURE_STATUSES.has(status);
}

/**
 * The subset of "signed" statuses at which a signature needs no further action from
 * anyone. `WAITING_FOR_WITNESS_SIGNATURES` is deliberately excluded: that signature is
 * still blocked on a witness, and an admin can drop the witness requirement to unblock it.
 *
 * Hardcoded for the same reason as `SIGNED_SIGNATURE_STATUSES` above.
 */
export const SETTLED_SIGNATURE_STATUSES: ReadonlySet<string> = new Set<string>([
	'SIGNED',
	'COMPLETED',
]);

/**
 * Returns true when `status` is settled — signed, with nothing outstanding on it.
 *
 * Prefer this over `isSignedSignatureStatus` when deciding whether an action is still
 * available; use `isSignedSignatureStatus` when deciding whether the signer has put pen
 * to paper (progress counts, "Complete" chips).
 */
export function isSettledSignatureStatus(status: string | null | undefined): boolean {
	return status != null && SETTLED_SIGNATURE_STATUSES.has(status);
}

/**
 * The "signed" statuses plus `CLOSED_WITHOUT_SIGNATURE` — every state in which a slot will
 * never receive another signing action.
 *
 * DC-1098 added closing a document without a signature: the slot is terminal, but the signer
 * never signed. Gate *actions* on this predicate (Sign / Resend / claim / assign, and which
 * file a wet-ink document resolves to); keep using `isSignedSignatureStatus` when asking
 * whether the signer actually put pen to paper — progress counts, "Complete" chips, and the
 * wet-ink page merge must not treat a closed slot as signed.
 *
 * Hardcoded for the same reason as `SIGNED_SIGNATURE_STATUSES` above.
 */
export const CONCLUDED_SIGNATURE_STATUSES: ReadonlySet<string> = new Set<string>([
	...SIGNED_SIGNATURE_STATUSES,
	'CLOSED_WITHOUT_SIGNATURE',
]);

/** Returns true when nothing is left for this slot's signer to do — signed, or closed. */
export function isConcludedSignatureStatus(status: string | null | undefined): boolean {
	return status != null && CONCLUDED_SIGNATURE_STATUSES.has(status);
}

/**
 * The statuses at which a signature is still waiting on its signer.
 *
 * Deliberately narrower than `!isConcludedSignatureStatus`: DECLINED, EXPIRED and ERROR are
 * neither concluded nor outstanding, so a naive negation gets them wrong.
 *
 * Hardcoded for the same reason as `SIGNED_SIGNATURE_STATUSES` above.
 */
export const OUTSTANDING_SIGNATURE_STATUSES: ReadonlySet<string> = new Set<string>([
	'CREATED',
	'PENDING_SIGNATURE',
]);

/** Returns true when this slot's signer still has something to do. */
export function isOutstandingSignatureStatus(status: string | null | undefined): boolean {
	return status != null && OUTSTANDING_SIGNATURE_STATUSES.has(status);
}
